import QuizRubricOptions from 'components/CreateQuiz/QuizRubricOptions';
import { PageContext } from 'pages/EditQuiz/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Stack, Text } from 'shell/components';
import { ViewportDevice } from 'utils/constants';
import { hasQuizRubric } from 'utils/quizRubric';

const QuizDetailsSettings = ({ device }) => {
    const { t } = useTranslation(['quiz', 'assignment', 'common']);
    const {
        form,
        setForm,
        isEditable = true,
        isResyncing,
        setRubricDialog,
        handleDeleteRubric,
        handleMapCriteriaByOrderChange,
        handleResyncQuiz,
        handleRubricTypeChange,
    } = useContext(PageContext);
    const hasRubric = hasQuizRubric(form.rubric);

    return (
        <Stack component={'section'} aria-labelledby={'quiz-details-heading'} spacing={5}>
            <Stack direction={'row'} alignItems={'center'} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Text component={'h2'} id={'quiz-details-heading'} variant={'p1'} weight={'medium'}>
                    {t('quiz:edit.details-title', 'Quiz Details')}
                </Text>
                <Button
                    variant={'borderless'}
                    color={'neutral'}
                    size={'small'}
                    startIcon={<Icon kind={'refresh'} />}
                    loading={isResyncing}
                    disabled={!isEditable || isResyncing}
                    onClick={handleResyncQuiz}
                    sx={{ px: 3, py: 1.5 }}
                >
                    {t('quiz:create.resync', 'Resync from LMS')}
                </Button>
            </Stack>

            <Stack spacing={2}>
                <Text component={'h3'} variant={'p2'} weight={'medium'}>
                    {t('quiz:rubric.title', 'Quiz Rubric')}
                    <Text component={'span'} color={'error'} aria-hidden={'true'}>
                        *
                    </Text>
                </Text>
                {hasRubric ? (
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={2}>
                        <Text color={'light'} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            {form.rubric.title}
                        </Text>
                        <Stack direction={'row'} spacing={2} sx={{ flexShrink: 0 }}>
                            <IconButton
                                aria-label={t('assignment:general-section.edit-rubric', 'Edit rubric')}
                                kind={'edit'}
                                size={'small'}
                                tooltip={{ component: t('common:controls.edit', 'Edit') }}
                                variant={'square'}
                                disabled={!isEditable}
                                onClick={() => setRubricDialog('edit')}
                            />
                            <IconButton
                                aria-label={t('assignment:general-section.delete-rubric', 'Delete rubric')}
                                kind={'delete'}
                                size={'small'}
                                tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                variant={'square'}
                                disabled={!isEditable}
                                onClick={handleDeleteRubric}
                            />
                        </Stack>
                    </Stack>
                ) : (
                    <Stack
                        direction={device === ViewportDevice.MOBILE ? 'column' : 'row'}
                        alignItems={device === ViewportDevice.MOBILE ? 'flex-start' : 'center'}
                        justifyContent={'space-between'}
                        spacing={3}
                    >
                        <Text role={'alert'} color={'error'}>
                            {t(
                                'quiz:rubric.missing',
                                'Rubric missing. You can either create a new rubric or reimport the quiz with the rubric included. Without this, the quiz cannot be created.'
                            )}
                        </Text>
                        <Button disabled={!isEditable} onClick={() => setRubricDialog('create')} sx={{ flexShrink: 0 }}>
                            {t('quiz:rubric.create', 'Create rubric')}
                        </Button>
                    </Stack>
                )}
            </Stack>

            {hasRubric && (
                <QuizRubricOptions
                    device={device}
                    disabled={!isEditable}
                    form={form}
                    setForm={setForm}
                    handleRubricTypeChange={handleRubricTypeChange}
                    handleMapCriteriaByOrderChange={handleMapCriteriaByOrderChange}
                />
            )}
        </Stack>
    );
};

export default QuizDetailsSettings;
